/** Plovoucí přesouvatelný panel se všemi topicy z DB. Klik = copy do schránky. Globální FAB toggle. */

import { Copy, GripHorizontal, Library, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAllTopics } from "@client/hooks/useAllTopics";

const fmt = (v: unknown): string => (v === undefined || v === null ? "—" : typeof v === "boolean" ? (v ? "true" : "false") : String(v));

export const TopicLibraryPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: 0 }); // offset od výchozí pozice (vpravo dole)
  const [q, setQ] = useState("");
  const dragRef = useRef<{ sx: number; sy: number; ox: number; oy: number } | null>(null);
  const topics = useAllTopics();

  useEffect(() => {
    const move = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setPos({ x: d.ox + (e.clientX - d.sx), y: d.oy + (e.clientY - d.sy) });
    };
    const up = () => (dragRef.current = null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, []);

  const filtered = q
    ? topics.filter((t) => `${t.nickname} ${t.topic} ${t.unit}`.toLowerCase().includes(q.toLowerCase()))
    : topics;

  const copy = (topic: string) => {
    void navigator.clipboard?.writeText(topic);
    toast.success(`Zkopírováno: ${topic}`);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-4 right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:opacity-90"
        aria-label="Knihovna topiců"
        title="Knihovna topiců"
      >
        <Library className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-4 right-4 z-40 flex max-h-[70vh] w-[min(24rem,92vw)] flex-col rounded-lg border border-border bg-card shadow-2xl"
      style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}
    >
      <div
        className="flex cursor-move items-center gap-2 rounded-t-lg border-b border-border bg-muted/50 px-3 py-2"
        onPointerDown={(e) => {
          dragRef.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y };
        }}
      >
        <GripHorizontal className="h-4 w-4 text-muted-foreground" />
        <span className="flex-1 text-sm font-semibold">Knihovna topiců</span>
        <button type="button" onClick={() => setOpen(false)} aria-label="Zavřít" className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-2">
        <input
          className="w-full rounded border border-input bg-background px-2 py-1 text-sm"
          placeholder="Hledat…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-2">
        {filtered.map((t) => (
          <button
            key={t.topic}
            type="button"
            onClick={() => copy(t.topic)}
            className="group flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-accent"
            title="Kliknutím zkopírovat topic"
          >
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-xs font-medium">{t.nickname || t.topic}</span>
              <span className="truncate font-mono text-[10px] text-muted-foreground">{t.topic}</span>
            </div>
            <span className="shrink-0 text-right text-[10px] text-muted-foreground">
              {fmt(t.lastValue)} {t.unit}
            </span>
            <Copy className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover:opacity-60" />
          </button>
        ))}
        {filtered.length === 0 && <p className="py-3 text-center text-xs text-muted-foreground">Nic nenalezeno.</p>}
      </div>
    </div>
  );
};

export default TopicLibraryPanel;
